import { useCallback, useEffect, useState } from 'react';

// The colour theme (Blue or Green), chosen by each user under their profile menu. The light/dark
// switch (useTheme) is separate, and the two combine: Green in dark mode is a thing.
//
// The theme is a class on <html> that swaps the CSS variables behind Tailwind's emerald, teal and
// navy scales (src/theme/colorThemes.css). Blue, the logo's blues, is the default and has no class.
//
// Remembered per user on this computer, because reception PCs are shared: each person who signs in
// gets their own choice back. The last choice made on the computer is also kept on its own, for the
// login screen, which shows before anyone has signed in, and for public/index.html, which applies
// it before the first paint so the wrong colours never flash.

export const COLOR_THEMES = [
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
];

const DEFAULT = 'blue';
const LAST_KEY = 'clinic_color_theme';
const userKey = (username) => `clinic_color_theme:${username}`;

const valid = (v) => (COLOR_THEMES.some((t) => t.value === v) ? v : null);

const read = (key) => {
  try {
    return valid(localStorage.getItem(key));
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage blocked or full: the theme still applies for this visit.
  }
};

export const applyColorTheme = (theme) => {
  const el = document.documentElement;
  COLOR_THEMES.forEach((t) => el.classList.remove(`theme-${t.value}`));
  if (theme && theme !== DEFAULT) el.classList.add(`theme-${theme}`);
};

/**
 * @param {string|undefined} username the signed-in user, whose own choice is loaded when they sign in
 */
export default function useColorTheme(username) {
  const [colorTheme, setState] = useState(() => read(LAST_KEY) || DEFAULT);

  // Someone signs in: their own choice wins, if they ever made one on this computer.
  useEffect(() => {
    if (!username) return;
    const own = read(userKey(username));
    if (own) setState(own);
  }, [username]);

  useEffect(() => {
    applyColorTheme(colorTheme);
  }, [colorTheme]);

  const setColorTheme = useCallback(
    (value) => {
      const theme = valid(value) || DEFAULT;
      setState(theme);
      write(LAST_KEY, theme);
      if (username) write(userKey(username), theme);
    },
    [username]
  );

  return { colorTheme, setColorTheme };
}
